import { CheckCircle } from "lucide-react";
import { DocRow, EmptyState, SavingBanner, FreshnessNote, GenerateButton } from "./shared";
import s from "./documents.module.css";

/**
 * Booking Documents panel — booking, insurance & SSR confirmations
 * (document_type BOOKING, spec §8.3).
 *
 * One action generates the whole family from booking data — the booking
 * confirmation (§8.3.1) always, plus the services & preferences confirmation
 * (§8.3.2) when the booking has special services. Both are one-per-booking and
 * rebuilt in place on regenerate, so they keep their immutable numbers.
 * Insurance confirmations (§8.3.3) come from their own flow and only appear here.
 */
const ConfirmationsPanel = ({
  docs, generating, onGenerate, onDownload, onView, onSend, downloading, newDocIds,
  lastGeneratedAt, isStale,
}) => {
  const hasPendingSave = docs.some((d) => !d.pdfUrl && !d.pdfStuck);
  const generated = docs.filter((d) =>
    ["booking_confirmation", "ssr_confirmation", "insurance_confirmation"].includes(d.documentSubtype));

  return (
    <div>
      <div className={s.panelHead}>
        <div>
          <div className={s.panelTitle}><CheckCircle size={16} /> Booking Documents</div>
          <div className={s.panelSubtitle}>
            {docs.length} document{docs.length !== 1 ? "s" : ""} · booking, insurance &amp; SSR confirmations
          </div>
        </div>
        {onGenerate && (
          <div className={s.panelActions}>
            <GenerateButton
              count={generated.length}
              generating={generating}
              onClick={onGenerate}
              noun="Booking Documents"
              disabled={hasPendingSave}
            />
          </div>
        )}
      </div>

      {docs.length === 0 ? (
        <EmptyState
          Icon={CheckCircle}
          title="No booking documents yet"
          hint="Generate the booking confirmation and the services & preferences confirmation for this booking. Insurance confirmations appear here once they are issued."
        />
      ) : (
        <>
          <FreshnessNote
            lastGeneratedAt={lastGeneratedAt}
            isStale={isStale}
            onRegenerate={onGenerate}
            regenerating={generating}
            pending={hasPendingSave}
          />
          <div className={s.list}>
            {docs.map((doc, i) => (
              <DocRow
                key={doc.id}
                doc={doc}
                index={i}
                isNew={newDocIds.has(doc.id)}
                onDownload={onDownload}
                onView={onView}
                onSend={onSend}
                downloading={downloading}
              />
            ))}
            {hasPendingSave && <SavingBanner />}
          </div>
        </>
      )}
    </div>
  );
};

export default ConfirmationsPanel;
